import {ColumnsType} from "antd/lib/table";
import {
    ApplicationUserDto,
    DeliveryMethod,
    OrderDto,
    OrderPaymentMethod,
    OrderPaymentStatus,
    OrderStage
} from "../../api";
import {CrudTableWrapperProps} from "digimuth-components/dist/components/crudTable/crudTable";
import {AsyncButton, AsyncButtonPopconfirm, CrudTable, nameof} from "digimuth-components/dist";
import {Button, Tag} from "antd";
import {generatePath, useNavigate} from "react-router-dom";
import {RoutingPaths} from "../routing/routingContainer";
import styles from "./orderListContainer.module.scss";
import {getColumnSearchProps} from "../../helpers/getColumnSearchProps";
import OrderStageTranslation, {
    OrderPaymentStageTranslation,
    OrderPaymentTag,
    OrderStageTag
} from "../../helpers/OrderStageHelper";
import deliveryMethodTranslation from "../../helpers/deliveryMethodHelper";
import {OrderDeleteInfo} from "./orderListContainer";

interface OrdersListProps extends CrudTableWrapperProps<OrderDto> {
    onDelete: (orderDeleteInfo: OrderDeleteInfo) => Promise<any>;
}

export default function OrdersList(props: OrdersListProps) {
    const navigate = useNavigate();

    const onOrderProductsClicked = (orderId: number) => {
        navigate(generatePath(RoutingPaths.orderDetails.route, {
            orderId: orderId.toString()
        }));
    }

    const columns: ColumnsType<OrderDto> = [
        {
            key: nameof<OrderDto>("orderNumber"),
            dataIndex: nameof<OrderDto>("orderNumber"),
            title: "Numer zamówienia",
            ...getColumnSearchProps('orderNumber')
        },
        {
            key: nameof<OrderDto>("orderStage"),
            dataIndex: nameof<OrderDto>("orderStage"),
            title: "Etap zamówienia",
            filters: [
                {text: OrderStageTranslation(OrderStage.New), value: OrderStage.New},
                {text: OrderStageTranslation(OrderStage.Paid), value: OrderStage.Paid},
                {text: OrderStageTranslation(OrderStage.WaitingForPayment), value: OrderStage.WaitingForPayment},
                {text: OrderStageTranslation(OrderStage.Sent), value: OrderStage.Sent},
                {text: OrderStageTranslation(OrderStage.Delivered), value: OrderStage.Delivered},
                {text: OrderStageTranslation(OrderStage.Abandoned), value: OrderStage.Abandoned}
            ],
            onFilter: (value, record) => record.orderStage === value,
            render: (stage) => OrderStageTag(stage)
        },
        {
            key: nameof<OrderDto>("orderPaymentStatus"),
            dataIndex: nameof<OrderDto>("orderPaymentStatus"),
            title: "Status płatności",
            filters: [
                {text: OrderPaymentStageTranslation(OrderPaymentStatus.Canceled), value: OrderPaymentStatus.Canceled},
                {
                    text: OrderPaymentStageTranslation(OrderPaymentStatus.Completed),
                    value: OrderPaymentStatus.Completed
                },
                {text: OrderPaymentStageTranslation(OrderPaymentStatus.Pending), value: OrderPaymentStatus.Pending},
                {
                    text: OrderPaymentStageTranslation(OrderPaymentStatus.WaitingForConfimration),
                    value: OrderPaymentStatus.WaitingForConfimration
                }
            ],
            onFilter: (value, record) => record.orderPaymentStatus === value,
            render: (paymentStatus) => OrderPaymentTag(paymentStatus)
        },
        {
            key: nameof<OrderDto>("deliveryMethod"),
            dataIndex: nameof<OrderDto>("deliveryMethod"),
            title: "Sposób dostawy",
            filters: [
                {text: deliveryMethodTranslation(DeliveryMethod.SelfPickup), value: DeliveryMethod.SelfPickup},
                {text: deliveryMethodTranslation(DeliveryMethod.InPostCarrier), value: DeliveryMethod.InPostCarrier},
                {
                    text: deliveryMethodTranslation(DeliveryMethod.InPostParcelMachine),
                    value: DeliveryMethod.InPostParcelMachine
                }
            ],
            onFilter: (value, record) => record.deliveryMethod === value,
            render: (method) => deliveryMethodTranslation(method)
        },
        {
            key: nameof<OrderDto>("client") || nameof<OrderDto>("unregisteredUserData"),
            dataIndex: nameof<OrderDto>("client") || nameof<OrderDto>("unregisteredUserData"),
            title: "Klient",
            render: (type: ApplicationUserDto, record: OrderDto) => {
                if (record.clientId === null && record.unregisteredUserData !== null) {
                    return `${record.unregisteredUserData?.firstName} ${record.unregisteredUserData?.lastName}`;
                } else if (type !== null) {
                    return `${type.firstName} ${type.lastName}`;
                } else {
                    return "";
                }
            }
        },
        {
            key: nameof<OrderDto>("orderValue"),
            dataIndex: nameof<OrderDto>("orderValue"),
            title: "Kwota zamówienia (zł)",
            render: (value: number) => value.toFixed(2)
        },
        {
            key: nameof<OrderDto>("paymentMethod"),
            dataIndex: nameof<OrderDto>("paymentMethod"),
            title: "Metoda płatności",
            render: (method: OrderPaymentMethod) => method?.name
        },
        {
            key: nameof<OrderDto>("optionalMessage"),
            dataIndex: nameof<OrderDto>("optionalMessage"),
            title: "Wiadomość do zamówienia",
            render: (message: string | null) => message
                ? <Tag color={"yellow"}>Tak</Tag>
                : <Tag color={"blue"}>Nie</Tag>
        }
    ];

    const canOrderBeDeleted = (stage: OrderStage) => {
        switch (stage) {
            case OrderStage.Paid:
            case OrderStage.WaitingForPayment:
            case OrderStage.New:
            case OrderStage.Abandoned:
            case OrderStage.Confirmed:
                return true;
            default:
                return false;
        }
    }

    const canOrderBeEdited = (stage: OrderStage, deliveryMethod: DeliveryMethod, paymentMethod: OrderPaymentMethod) => {
        switch (stage) {
            case OrderStage.New:
                return true;
            case OrderStage.WaitingForPayment:
            case OrderStage.Confirmed:
                return deliveryMethod === DeliveryMethod.SelfPickup && paymentMethod.value === "ShopPayment";
            default:
                return false;
        }
    }

    const shouldShowRefundOnDelete = (order: OrderDto) => {
        if (!order.paymentMethod || order.paymentMethod?.value === "ShopPayment") return false;
        switch (order.orderPaymentStatus) {
            case OrderPaymentStatus.Completed:
                return true;
            default:
                return false;
        }
    }

    const shouldCancelOrder = (orderStage: OrderStage, paymentStage: OrderPaymentStatus) => {
        if (orderStage === OrderStage.Paid || orderStage === OrderStage.WaitingForPayment || orderStage === OrderStage.Confirmed) {
            return paymentStage === OrderPaymentStatus.Completed;
        }
    }

    return <CrudTable
        data={props.data}
        isLoading={props.isLoading}
        columns={columns}
        customRowActions={record => <div>
            <Button style={{width: "100%"}} type={"primary"} onClick={() => onOrderProductsClicked(record.id!)}>
                Szczegóły
            </Button>
            <div className={styles.customRowActionButtonsContainer}>
                <AsyncButton
                    style={{width: "50%"}}
                    onClick={() => props.onEditClicked?.(record.id!)}
                    disabled={!canOrderBeEdited(record.orderStage!, record.deliveryMethod!, record.paymentMethod!)}
                >
                    Edytuj
                </AsyncButton>
                <AsyncButtonPopconfirm
                    title={shouldCancelOrder(record.orderStage!, record.orderPaymentStatus!)
                        ? "Czy na pewno chcesz anulować to zamówienie?"
                        : "Czy na pewno chcesz usunąć to zamówienie?"
                    }
                    danger buttonProps={{style: {width: "50%"}}}
                    onConfirm={() => props.onDelete({
                            orderId: record.id!,
                            orderGuid: record.orderGuid!,
                            orderNumber: record.orderNumber!,
                            showRefund: shouldShowRefundOnDelete(record),
                            refundValue: record.orderValue! - (record.refundsSum ?? 0)
                        }
                    )}
                    okText={shouldCancelOrder(record.orderStage!, record.orderPaymentStatus!) ? "Tak" : "Usuń"}
                    disabled={!canOrderBeDeleted(record.orderStage!)}
                    cancelText={shouldCancelOrder(record.orderStage!, record.orderPaymentStatus!) ? "Nie" : "Anuluj"}
                >
                    {shouldCancelOrder(record.orderStage!, record.orderPaymentStatus!) ? "Anuluj" : "Usuń"}
                </AsyncButtonPopconfirm>
            </div>
        </div>}
    />;
}
